/**
 * UI language: the person's own choice, kept in this browser (like the theme). English is built into the bundle
 * and is the fallback for any missing word; every other language is a JSON file in `public/locales/`, listed in
 * `public/locales/index.json`, that can be edited or added without touching code.
 */
export const LANG_STORAGE_KEY = 'mf.lang';
export const DEFAULT_LANG = 'mn';
export const SOURCE_LANG = 'en';

export interface LanguageOption {
  code: string;
  /** The language's own name ("Монгол", "English"), shown as is in every UI language. */
  name: string;
}

/** Used when `index.json` cannot be loaded, so the switch still offers the two shipped languages. */
export const FALLBACK_LANGUAGES: LanguageOption[] = [
  { code: 'mn', name: 'Монгол' },
  { code: 'en', name: 'English' },
];

const LANG_CODE = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/;

/** The stored choice, or the default. Ignores anything that is not a language code. */
export function readLang(storage: Pick<Storage, 'getItem'> | null | undefined): string {
  try {
    const v = storage?.getItem(LANG_STORAGE_KEY);
    if (v && LANG_CODE.test(v)) return v;
  } catch {
    // private mode / blocked storage — use the default
  }
  return DEFAULT_LANG;
}

/** `index.json` → valid options only; null when the payload is not a usable list. */
export function parseLanguageList(data: unknown): LanguageOption[] | null {
  if (!Array.isArray(data)) return null;
  const out = data.filter(
    (x): x is LanguageOption =>
      !!x && typeof x.code === 'string' && LANG_CODE.test(x.code) && typeof x.name === 'string' && x.name.trim() !== '',
  );
  return out.length ? out : null;
}

type Tree = { [k: string]: string | Tree };

/** Nested translation object → { 'a.b.c': text }. */
export function flatten(tree: Tree, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(tree)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === 'string') out[key] = v;
    else Object.assign(out, flatten(v, key));
  }
  return out;
}

const placeholders = (s: string) => [...s.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)].map((m) => m[1]).sort();
const tags = (s: string) => [...s.matchAll(/<\/?([a-z][a-z0-9]*)\s*\/?>/gi)].map((m) => m[0].replace(/\s/g, '')).sort();

export interface LocaleProblems {
  missing: string[];
  extra: string[];
  /** Keys whose {{placeholders}} differ from English — the text would show a raw `{{name}}` or drop a value. */
  placeholders: string[];
  /** Keys whose <b>…</b> tags differ from English — <Trans> would drop the bold or show raw tags. */
  markup: string[];
  empty: string[];
}

/** Compare a translation against the English source. A shipped language must have no problems at all. */
export function checkLocale(source: Tree, other: Tree): LocaleProblems {
  const s = flatten(source);
  const o = flatten(other);
  return {
    missing: Object.keys(s).filter((k) => !(k in o)),
    extra: Object.keys(o).filter((k) => !(k in s)),
    placeholders: Object.keys(s).filter((k) => k in o && placeholders(s[k]).join() !== placeholders(o[k]).join()),
    markup: Object.keys(s).filter((k) => k in o && tags(s[k]).join() !== tags(o[k]).join()),
    empty: Object.keys(o).filter((k) => o[k].trim() === ''),
  };
}

/** A language file fetched from nginx; a missing file comes back as the SPA's HTML, which must count as empty. */
export function parseLocaleFile(text: string): Tree {
  try {
    const v: unknown = JSON.parse(text);
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Tree) : {};
  } catch {
    return {};
  }
}
